import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    History,
    Upload,
    Copy,
    Loader2,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    Plus,
    Trash2,
    CheckCircle2,
    RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtEUR, fmtDate } from "@/lib/api";

export default function RevisionsDialog({ devis, onChanged }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);
    const [note, setNote] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.listRevisions(devis.id);
            setData(r);
        } catch {
            toast.error("Impossible de charger l'historique");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line
    }, [open]);

    const duplicate = async () => {
        setBusy(true);
        try {
            await api.duplicateAsRevision(devis.id, note || null);
            toast.success("Nouvelle version créée");
            setNote("");
            await load();
            onChanged && onChanged();
        } catch {
            toast.error("Erreur lors de la duplication");
        } finally {
            setBusy(false);
        }
    };

    const uploadPdf = async () => {
        if (!pdfFile) {
            toast.error("Sélectionnez un PDF");
            return;
        }
        setBusy(true);
        try {
            await api.uploadRevision(devis.id, pdfFile, "claude", note || null);
            toast.success(
                "Nouvelle version importée — l'IA a extrait les postes"
            );
            setPdfFile(null);
            setNote("");
            await load();
            onChanged && onChanged();
        } catch {
            toast.error("Erreur lors de l'import du PDF");
        } finally {
            setBusy(false);
        }
    };

    const activate = async (vid) => {
        setBusy(true);
        try {
            await api.activateVersion(vid);
            toast.success("Version activée");
            await load();
            onChanged && onChanged();
        } catch {
            toast.error("Erreur");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    className="p-2 text-ink-soft hover:text-accent transition-colors relative"
                    title="Historique des versions"
                    data-testid={`revisions-btn-${devis.id}`}
                >
                    <History className="w-4 h-4" strokeWidth={1.6} />
                    {(devis.version || 1) > 1 && (
                        <span
                            className="absolute -top-1 -right-0.5 text-[9px] font-bold text-accent bg-white px-1 leading-tight border border-accent"
                            data-testid={`version-badge-${devis.id}`}
                        >
                            V{devis.version}
                        </span>
                    )}
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-none">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-extrabold">
                        Historique des versions —{" "}
                        <span className="text-accent">
                            {devis.contractor_name}
                        </span>
                    </DialogTitle>
                </DialogHeader>

                {/* Create new version */}
                <section className="panel p-4 space-y-3">
                    <div className="stat-label">Créer une nouvelle version</div>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Note (facultatif) : ex. « V2 — après 1ère relance, -5% sur la plomberie »"
                        rows={2}
                        className="rounded-none border-ink/30"
                        data-testid="revision-note-input"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="border border-ink/40 p-3 space-y-2">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                                <Upload className="w-3.5 h-3.5" strokeWidth={2} />
                                Importer un nouveau PDF (IA)
                            </div>
                            <Input
                                type="file"
                                accept=".pdf"
                                onChange={(e) =>
                                    setPdfFile(e.target.files?.[0] || null)
                                }
                                className="rounded-none h-9 text-xs"
                                data-testid="revision-pdf-input"
                            />
                            <Button
                                onClick={uploadPdf}
                                disabled={!pdfFile || busy}
                                className="w-full rounded-none bg-accent hover:bg-accent-hover"
                                data-testid="revision-upload-btn"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Importer & créer V" +
                                    (((data?.versions || []).at(-1)?.version || 1) + 1)
                                )}
                            </Button>
                        </div>
                        <div className="border border-ink/40 p-3 space-y-2">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                                <Copy className="w-3.5 h-3.5" strokeWidth={2} />
                                Dupliquer la version actuelle
                            </div>
                            <p className="text-xs text-ink-soft">
                                Crée une copie modifiable où vous pourrez
                                ajuster manuellement les montants.
                            </p>
                            <Button
                                onClick={duplicate}
                                disabled={busy}
                                variant="outline"
                                className="w-full rounded-none border-ink"
                                data-testid="revision-duplicate-btn"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Dupliquer en V" +
                                    (((data?.versions || []).at(-1)?.version || 1) + 1)
                                )}
                            </Button>
                        </div>
                    </div>
                </section>

                {/* Versions list */}
                {loading ? (
                    <div className="p-8 text-center text-sm text-ink-soft">
                        Chargement…
                    </div>
                ) : (
                    <section className="space-y-4">
                        {(data?.versions || []).map((v) => (
                            <VersionCard
                                key={v.id}
                                v={v}
                                onActivate={() => activate(v.id)}
                                busy={busy}
                            />
                        ))}
                        {(data?.versions || []).length === 0 && (
                            <div className="p-8 text-center text-sm text-ink-soft">
                                Aucune version
                            </div>
                        )}
                    </section>
                )}
            </DialogContent>
        </Dialog>
    );
}

function VersionCard({ v, onActivate, busy }) {
    const diff = v.diff_vs_previous;
    return (
        <div
            className={`panel ${v.is_active ? "border-2 border-accent" : ""}`}
            data-testid={`version-card-${v.version}`}
        >
            <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="font-heading text-xl font-extrabold">
                        V{v.version}
                    </div>
                    {v.is_active && (
                        <span className="pill border-accent text-accent bg-accent/10">
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
                            Active
                        </span>
                    )}
                    <span className="text-xs text-ink-soft">
                        {fmtDate(v.created_at)}
                    </span>
                    {v.source === "pdf" && v.pdf_filename && (
                        <span className="text-xs text-ink-soft truncate">
                            · {v.pdf_filename}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="num text-lg font-extrabold">
                            {fmtEUR(v.total_ht)}
                        </div>
                        {diff && diff.delta_total !== 0 && (
                            <DeltaPill
                                delta={diff.delta_total}
                                pct={diff.pct_total}
                            />
                        )}
                    </div>
                    {!v.is_active && (
                        <Button
                            onClick={onActivate}
                            disabled={busy}
                            variant="outline"
                            size="sm"
                            className="rounded-none border-ink"
                            data-testid={`activate-v${v.version}-btn`}
                        >
                            <RotateCcw
                                className="w-3.5 h-3.5 mr-1.5"
                                strokeWidth={1.6}
                            />
                            Activer
                        </Button>
                    )}
                </div>
            </div>

            {v.revision_note && (
                <div className="px-5 py-2 bg-warn/10 border-b border-warn/30 text-sm">
                    <b>Note :</b> {v.revision_note}
                </div>
            )}

            {diff && (
                <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-[#FAFAFA] border-b border-border">
                    <DiffStat
                        icon={<Minus className="w-3 h-3" strokeWidth={2} />}
                        label="Inchangés"
                        value={diff.unchanged_count}
                        color="text-ink-soft"
                    />
                    <DiffStat
                        icon={<ArrowUpRight className="w-3 h-3" strokeWidth={2} />}
                        label="Modifiés"
                        value={diff.changed.length}
                        color="text-warn"
                    />
                    <DiffStat
                        icon={<Plus className="w-3 h-3" strokeWidth={2} />}
                        label="Ajoutés"
                        value={diff.added.length}
                        color="text-ok"
                    />
                    <DiffStat
                        icon={<Trash2 className="w-3 h-3" strokeWidth={2} />}
                        label="Supprimés"
                        value={diff.removed.length}
                        color="text-danger"
                    />
                </div>
            )}

            {diff && (diff.changed.length || diff.added.length || diff.removed.length) > 0 && (
                <div className="px-5 py-3 space-y-3 text-sm">
                    {diff.changed.length > 0 && (
                        <div>
                            <div className="stat-label mb-1">Postes modifiés</div>
                            <div className="space-y-1">
                                {diff.changed.map((c, i) => (
                                    <div
                                        key={`${c.description || ""}-${i}`}
                                        className="flex items-center justify-between gap-3 border-b border-border/50 py-1"
                                        data-testid={`changed-row-v${v.version}-${i}`}
                                    >
                                        <div className="truncate flex-1">
                                            {c.description}
                                        </div>
                                        <div className="num text-ink-soft text-xs">
                                            {fmtEUR(c.prev_total)} →{" "}
                                            <b className="text-ink">
                                                {fmtEUR(c.curr_total)}
                                            </b>
                                        </div>
                                        <DeltaPill delta={c.delta} pct={c.pct} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {diff.added.length > 0 && (
                        <div>
                            <div className="stat-label mb-1 text-ok">
                                Postes ajoutés
                            </div>
                            <ul className="text-xs space-y-1">
                                {diff.added.map((a, i) => (
                                    <li key={`${a.description || ""}-${i}`} className="flex justify-between">
                                        <span className="truncate">{a.description}</span>
                                        <span className="num">{fmtEUR(a.total)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {diff.removed.length > 0 && (
                        <div>
                            <div className="stat-label mb-1 text-danger">
                                Postes supprimés
                            </div>
                            <ul className="text-xs space-y-1">
                                {diff.removed.map((r, i) => (
                                    <li key={`${r.description || ""}-${i}`} className="flex justify-between text-ink-soft">
                                        <span className="truncate line-through">
                                            {r.description}
                                        </span>
                                        <span className="num">{fmtEUR(r.total)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function DeltaPill({ delta, pct }) {
    if (delta === 0 || delta == null)
        return <span className="num text-[11px] text-ink-soft">±0</span>;
    const positive = delta > 0;
    return (
        <span
            className={`num text-[11px] font-bold inline-flex items-center gap-0.5 ${
                positive ? "text-danger" : "text-ok"
            }`}
        >
            {positive ? (
                <ArrowUpRight className="w-3 h-3" strokeWidth={2.5} />
            ) : (
                <ArrowDownRight className="w-3 h-3" strokeWidth={2.5} />
            )}
            {positive ? "+" : ""}
            {fmtEUR(delta)}
            {pct != null && (
                <span className="ml-1 text-[10px]">
                    ({positive ? "+" : ""}
                    {pct}%)
                </span>
            )}
        </span>
    );
}

function DiffStat({ icon, label, value, color }) {
    return (
        <div className={`flex items-center gap-1.5 ${color}`}>
            {icon}
            <span className="text-xs uppercase tracking-wide">{label}</span>
            <span className="num font-bold ml-auto">{value}</span>
        </div>
    );
}
